/**
 * Reveals the "Read more" toggle on a review card only when its text is
 * actually clamped, and expands it in place when pressed.
 *
 * Overflow is measured by briefly removing the clamp and reading the full
 * height, rather than comparing scrollHeight to clientHeight. On an element
 * using -webkit-line-clamp those two can report the same value, which silently
 * hides the toggle on every card.
 *
 * Re-measured on resize and after webfonts load, because the clamp is a line
 * count: the same review fits at one column width and overflows at another.
 */
(function () {
  'use strict';

  var CARD = '.v4-rev';
  var MEASURING = 'v4-rev__text--measuring';

  function lineHeightOf(el, styles) {
    var lh = parseFloat(styles.lineHeight);
    if (!isNaN(lh) && lh > 0) return lh;
    // Computed line-height can come back as "normal"; approximate it.
    var fs = parseFloat(styles.fontSize);
    return (isNaN(fs) ? 16 : fs) * 1.4;
  }

  function measure(card) {
    var text = card.querySelector('.v4-rev__text');
    var button = card.querySelector('.v4-rev__more');
    if (!text || !button) return;

    // An expanded card is deliberately unclamped; leave its toggle showing.
    if (card.classList.contains('v4-rev--expanded')) {
      button.hidden = false;
      return;
    }

    var styles = window.getComputedStyle(text);
    var lines = parseInt(styles.getPropertyValue('--v4-rev-lines'), 10);
    if (isNaN(lines) || lines < 1) lines = 3;

    var allowed = lineHeightOf(text, styles) * lines;

    text.classList.add(MEASURING);
    var full = text.scrollHeight;
    text.classList.remove(MEASURING);

    // Fail open. If the measurement comes back as zero or the allowance can't
    // be worked out — layout not settled, card in an offscreen slide, odd
    // computed styles — show the toggle rather than silently swallowing it.
    // A toggle on a card that didn't need one is a far smaller problem than a
    // long review with no way to read the rest of it.
    if (!full || !isFinite(allowed) || allowed <= 0) {
      button.hidden = false;
      return;
    }

    button.hidden = full <= allowed + 1;
  }

  function toggle(button) {
    var card = button.closest(CARD);
    if (!card) return;

    var expanded = card.classList.toggle('v4-rev--expanded');
    var label = button.querySelector('.v4-rev__more-label');

    button.setAttribute('aria-expanded', String(expanded));
    if (label) {
      label.textContent = expanded
        ? button.getAttribute('data-label-less') || 'Read less'
        : button.getAttribute('data-label-more') || 'Read more';
    }

    if (!expanded) measure(card);
  }

  function init(root) {
    var cards = (root || document).querySelectorAll(CARD);
    Array.prototype.forEach.call(cards, measure);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('.v4-rev__more');
    if (button) toggle(button);
  });

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { init(); }, 150);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  window.addEventListener('load', function () { init(); });

  // Re-render in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Webfonts change line height, which changes what overflows.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { init(); });
  }
})();
